import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2, Phone, Mail, Clock, MapPin } from "lucide-react";
import { Reveal } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const priorities = ["low", "medium", "high", "critical"];

export const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", priority: "medium", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/tickets`, form);
      toast.success("Ticket submitted — our team will reach out shortly.");
      setForm({ name: "", email: "", priority: "medium", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-none border border-white/15 bg-black px-4 py-3.5 font-sans text-sm text-white placeholder:text-neutral-600 transition-colors duration-200 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary";

  return (
    <section id="support" data-testid="contact-section" className="border-b border-white/10 py-24 md:py-40">
      <div className="mx-auto grid w-full max-w-screen-2xl grid-cols-1 gap-12 px-6 md:grid-cols-12 md:px-12">
        <div className="md:col-span-5">
          <Reveal>
            <span className="label-mono">05 — Talk to us</span>
            <h2 className="mt-4 font-display text-3xl font-bold leading-none tracking-tight text-white md:text-5xl">
              Open a support ticket.
            </h2>
            <p className="mt-6 max-w-md font-sans text-base leading-relaxed text-neutral-400">
              Questions about deployment, licensing or scaling? Send us a ticket and a real engineer responds —
              typically within one business day.
            </p>

            <div className="mt-10 flex flex-col divide-y divide-white/10 border-y border-white/10">
              <a
                href="tel:+12068338370"
                data-testid="support-phone"
                className="group flex items-center gap-4 py-5 transition-colors duration-200 hover:text-primary"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-white/15 text-primary transition-colors duration-200 group-hover:border-primary">
                  <Phone className="h-4 w-4" strokeWidth={1.5} />
                </span>
                <span className="flex flex-col">
                  <span className="label-mono">Phone · Mon–Fri</span>
                  <span className="mt-1 font-mono text-base tracking-tight text-white transition-colors duration-200 group-hover:text-primary">
                    +1 206 833 8370
                  </span>
                </span>
              </a>
              <a
                href="mailto:ashtari12@jamilatechnology.com"
                data-testid="support-email"
                className="group flex items-center gap-4 py-5 transition-colors duration-200 hover:text-primary"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-white/15 text-primary transition-colors duration-200 group-hover:border-primary">
                  <Mail className="h-4 w-4" strokeWidth={1.5} />
                </span>
                <span className="flex flex-col">
                  <span className="label-mono">Email support</span>
                  <span className="mt-1 font-mono text-base tracking-tight text-white transition-colors duration-200 group-hover:text-primary">
                    ashtari12@jamilatechnology.com
                  </span>
                </span>
              </a>
              <div className="flex items-center gap-4 py-5" data-testid="support-response">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-white/15 text-primary">
                  <Clock className="h-4 w-4" strokeWidth={1.5} />
                </span>
                <span className="flex flex-col">
                  <span className="label-mono">Avg. response</span>
                  <span className="mt-1 font-mono text-base tracking-tight text-white">Under 1 business day</span>
                </span>
              </div>
              <a
                href="https://maps.google.com/?q=3601+26th+PL+W+Apt+425+Seattle+WA+98199"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="support-address"
                className="group flex items-start gap-4 py-5 transition-colors duration-200 hover:text-primary"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-white/15 text-primary transition-colors duration-200 group-hover:border-primary">
                  <MapPin className="h-4 w-4" strokeWidth={1.5} />
                </span>
                <span className="flex flex-col">
                  <span className="label-mono">Office</span>
                  <span className="mt-1 font-mono text-sm leading-relaxed tracking-tight text-white transition-colors duration-200 group-hover:text-primary">
                    3601 26th PL W, Apt #425
                    <br />
                    Seattle, WA 98199, US
                  </span>
                </span>
              </a>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-7">
          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="border border-white/10 bg-[#0A0A0A] p-8 md:p-10"
            >
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="label-mono mb-2 block">Name</label>
                  <input
                    data-testid="contact-name-input"
                    className={inputCls}
                    placeholder="Ada Lovelace"
                    value={form.name}
                    onChange={update("name")}
                  />
                </div>
                <div>
                  <label className="label-mono mb-2 block">Email</label>
                  <input
                    data-testid="contact-email-input"
                    type="email"
                    className={inputCls}
                    placeholder="ada@company.com"
                    value={form.email}
                    onChange={update("email")}
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="label-mono mb-3 block">Ticket Priority</label>
                <div className="flex flex-wrap gap-2">
                  {priorities.map((p) => (
                    <button
                      key={p}
                      type="button"
                      data-testid={`priority-${p}`}
                      onClick={() => setForm((f) => ({ ...f, priority: p }))}
                      className={`border px-4 py-2 font-mono text-xs uppercase tracking-widest transition-colors duration-200 ${
                        form.priority === p
                          ? "border-primary bg-primary text-white"
                          : "border-white/15 text-neutral-400 hover:border-white/40 hover:text-white"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <label className="label-mono mb-2 block">Message</label>
                <textarea
                  data-testid="contact-message-input"
                  rows={5}
                  className={`${inputCls} resize-none`}
                  placeholder="Tell us what you need help with…"
                  value={form.message}
                  onChange={update("message")}
                />
              </div>

              <motion.button
                type="submit"
                disabled={loading}
                whileTap={{ scale: 0.98 }}
                data-testid="contact-submit-button"
                className="group mt-8 flex w-full items-center justify-center gap-3 bg-primary px-8 py-4 font-mono text-sm uppercase tracking-widest text-white transition-colors duration-200 hover:bg-[#FF7733] disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Submitting
                  </>
                ) : (
                  <>
                    Submit ticket
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </>
                )}
              </motion.button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
